import type { Location } from 'src/models/index';
import type {
  GroupInfo,
  UserBase,
  GisLayer,
  ShareScopeLov,
  RemainingMovieRatesByDateRange,
  CustomGeoItemLayer,
  Settings,
  User,
  UserLoginComplete,
  UserLogin,
  UserLogout,
} from 'src/models/user';

export interface GroupInfoResponse {
  g1id: number;
  g1name: string;
  g2id: number;
  g2name: string;
  g3id: number;
  g3name: string;
}

export interface UserBaseResponse {
  id: number;
  username: string;
  display_name: string;
  role: number;
  group_id: number;
  active: number;
  timezone: string;
  default_page: string;
  group_info: GroupInfoResponse;
}

export interface UserLoginCompleteResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: UserBaseResponse;
}

export interface UserLoginTwoFactorResponse {
  two_factor_url: string | null;
}

export type UserLoginResponse = UserLoginCompleteResponse | UserLoginTwoFactorResponse;

export interface UserLogoutResponse {
  redirect_url?: string;
}

export interface GisLayerResponse {
  disp_name: string;
  layer_name: string;
}

export interface ShareScopeLovResponse {
  key: number;
  val: string;
  val_alt: string;
  disp_order: number;
}

export interface RemainingMovieRatesByDateRangeResponse {
  days_before_from: number;
  days_before_to: number;
  rate: number;
}

export interface CustomGeoItemLayerResponse {
  name: string;
  dispName: string;
  hideToggleCheckbox?: boolean;
  isCreatable?: boolean;
  isTabPanel?: boolean;
  tabLabel?: string;
}

export interface SettingsResponse {
  default_page: string;
  g1name: string;
  kp_set_name: string;
  initial_map_pos: Location;
  initial_extent: Location[];
  gis_layers: GisLayerResponse[];
  share_scope_lov: ShareScopeLovResponse[];
  ifd_link: string;
  remaining_movie_rates_by_date_ranges: RemainingMovieRatesByDateRangeResponse[];
  customGeoItemLayers: CustomGeoItemLayerResponse[];
}

export interface UserResponse extends UserBaseResponse {
  has_role_super_admin: boolean;
  has_role_operations_admin: boolean;
  has_role_branch_admin: boolean;
  has_role_department_admin: boolean;
  has_role_default: boolean;
  settings: SettingsResponse;
}

export type UserGetMeResponse = UserResponse;

export const toGroupInfo = (response: GroupInfoResponse): GroupInfo => ({
  g1id: response.g1id,
  g1name: response.g1name,
  g2id: response.g2id,
  g2name: response.g2name,
  g3id: response.g3id,
  g3name: response.g3name,
});

export const toUserBase = (response: UserBaseResponse): UserBase => ({
  id: response.id,
  username: response.username,
  displayName: response.display_name,
  role: response.role,
  groupId: response.group_id,
  active: response.active,
  timezone: response.timezone,
  defaultPage: response.default_page,
  groupInfo: toGroupInfo(response.group_info),
});

export const toGisLayer = (response: GisLayerResponse): GisLayer => ({
  dispName: response.disp_name,
  layerName: response.layer_name,
});

export const toShareScopeLov = (response: ShareScopeLovResponse): ShareScopeLov => ({
  key: response.key,
  val: response.val,
  valAlt: response.val_alt,
  dispOrder: response.disp_order,
});

export const toRemainingMovieRatesByDateRange = (
  response: RemainingMovieRatesByDateRangeResponse,
): RemainingMovieRatesByDateRange => ({
  daysBeforeFrom: response.days_before_from,
  daysBeforeTo: response.days_before_to,
  rate: response.rate,
});

export const toCustomGeoItemLayer = (response: CustomGeoItemLayerResponse): CustomGeoItemLayer => ({
  name: response.name,
  dispName: response.dispName,
  hideToggleCheckbox: response.hideToggleCheckbox ?? false,
  isCreatable: response.isCreatable ?? false,
  isTabPanel: response.isTabPanel ?? false,
  tabLabel: response.tabLabel ?? '',
});

export const toSettings = (response: SettingsResponse): Settings => ({
  defaultPage: response.default_page,
  g1name: response.g1name,
  kpSetName: response.kp_set_name,
  initialMapPos: response.initial_map_pos,
  initialExtent: response.initial_extent,
  gisLayers: response.gis_layers.map(toGisLayer),
  shareScopeLov: response.share_scope_lov.map(toShareScopeLov),
  ifdLink: response.ifd_link,
  remainingMovieRatesByDateRanges: response.remaining_movie_rates_by_date_ranges.map(toRemainingMovieRatesByDateRange),
  customGeoItemLayers: response.customGeoItemLayers.map(toCustomGeoItemLayer),
});

export const toUser = (response: UserResponse): User => ({
  ...toUserBase(response),
  hasRoleSuperAdmin: response.has_role_super_admin,
  hasRoleOperationsAdmin: response.has_role_operations_admin,
  hasRoleBranchAdmin: response.has_role_branch_admin,
  hasRoleDepartmentAdmin: response.has_role_department_admin,
  hasRoleDefault: response.has_role_default,
  settings: toSettings(response.settings),
});

export const toUserLoginComplete = (response: UserLoginCompleteResponse): UserLoginComplete => ({
  accessToken: response.access_token,
  tokenType: response.token_type,
  expiresIn: response.expires_in,
  user: toUserBase(response.user),
});

export const toUserLogin = (response: UserLoginResponse): UserLogin => {
  if ('access_token' in response) return toUserLoginComplete(response);
  return { twoFactorUrl: response.two_factor_url };
};

export const toUserLogout = (response: UserLogoutResponse): UserLogout => ({
  redirectUrl: response.redirect_url ?? null,
});
